import * as Errors from "../../../shared/node/Errors";

import {
  CreateBookingsWithSquareInvoiceDocument,
  CreateBookingsWithSquareInvoiceMutation,
  CreateBookingsWithSquareInvoiceMutationVariables,
} from "../GeneratedGraphQL";
import { defaultService, tiers } from "./service";
import {
  userDetailsSquareInvoice,
  userDetailsSquareInvoiceWithoutCrmData,
} from "./userDetails";

import { BookingData } from "../AppDataProvider/AppDataProvider";
import { defaultBookingTool } from "./bookingTool";
import { defaultRecurrence } from "./bookings";
import { format } from "date-fns";
import { getBookingQuestionInput } from "../utils";
import { GraphQLError } from "graphql";
import { MockedResponse } from "@apollo/client/testing";
import { successReservedSpaceId } from "./createBookingsForBookingTool";
import { testOrganisationSquare } from "./organisation";
// For completely inscrutable reasons, this doesn't work if you import from
// utils directly ¯\_(ツ)_/¯
import { today } from "../utils/today";

const timezone = "Europe/London";
export const successReservedSpaceWithSquareInvoiceId =
  "888777666555444333222124";
const errorReservedSpaceId = "888777666555444333222777";
const organisation = testOrganisationSquare;
export const successConfirmBookingWithSquareInvoiceId =
  "888777666555444333222332";

export const mockConfirmedBookingSquareInvoiceSuccessData: BookingData = {
  organisation,
  reservedSpaceId: successReservedSpaceWithSquareInvoiceId,
  startDate: new Date("2022-11-16T09:00:00.000Z"),
  timezone,
  userDetails: userDetailsSquareInvoice,
};

export const mockConfirmedBookingSquareInvoiceErrorData: BookingData = {
  organisation,
  reservedSpaceId: errorReservedSpaceId,
  startDate: new Date("2022-11-16T09:00:00.000Z"),
  timezone,
  userDetails: userDetailsSquareInvoice,
};

const requestVariables: CreateBookingsWithSquareInvoiceMutationVariables = {
  bookings: [
    {
      bookingNotes: "Square payment notes",
      bookingQuestions: getBookingQuestionInput({
        bookingQuestionData: {},
        bookingQuestions: organisation.externalBookingQuestions,
        pickedServiceId: defaultService._id,
      }),
      customer: {
        ...userDetailsSquareInvoice,
        crmFieldData: [
          { id: "date_field", value: format(today, "yyyy-MM-dd") },
          { id: "optional_field", value: "" },
          { id: "text_field", value: "this is a text field" },
        ],
      },
      organisationId: organisation._id,
      reservedSpaceId: successReservedSpaceWithSquareInvoiceId,
      timezone: timezone,
    },
  ],
  bookingToolId: defaultBookingTool._id,
  cardToken: "conf:mock",
  createdSource: "test-app",
  locale: "en-GB",
  squareBuyerVerificationToken: "verf:mock",
};

export const createBookingsWithSquareInvoiceRequestSuccess: MockedResponse<CreateBookingsWithSquareInvoiceMutation> =
  {
    request: {
      query: CreateBookingsWithSquareInvoiceDocument,
      variables: requestVariables,
    },
    result: {
      data: {
        createBookingsWithSquareInvoice: {
          __typename: "CreateBookingsResult",
          bookings: [{ _id: successConfirmBookingWithSquareInvoiceId }],
        },
      },
    },
  };

const multiTierRequestVariables: CreateBookingsWithSquareInvoiceMutationVariables =
  {
    bookings: [
      {
        bookingQuestions: getBookingQuestionInput({
          bookingQuestionData: {},
          bookingQuestions: testOrganisationSquare.externalBookingQuestions,
          pickedServiceId: defaultService._id,
        }),
        customer: {
          ...userDetailsSquareInvoice,
          crmFieldData: [
            { id: "date_field", value: format(today, "yyyy-MM-dd") },
            { id: "optional_field", value: "" },
            { id: "text_field", value: "this is a text field" },
          ],
        },
        organisationId: testOrganisationSquare._id,
        pricePoints: [
          { pricePointId: tiers[0].pricePoints[0]._id, quantity: 2 },
        ],
        reservedSpaceId: successReservedSpaceId,
        tierId: tiers[0]._id,
        timezone: timezone,
      },
    ],
    bookingToolId: defaultBookingTool._id,
    cardToken: "conf:mock",
    createdSource: "test-app",
    locale: "en-GB",
    squareBuyerVerificationToken: "verf:mock",
  };

export const createMultiTierBookingsWithSquareInvoiceRequestSuccess: MockedResponse<CreateBookingsWithSquareInvoiceMutation> =
  {
    request: {
      query: CreateBookingsWithSquareInvoiceDocument,
      variables: multiTierRequestVariables,
    },
    result: {
      data: {
        createBookingsWithSquareInvoice: {
          __typename: "CreateBookingsResult",
          bookings: [{ _id: successConfirmBookingWithSquareInvoiceId }],
        },
      },
    },
  };

const discountRequestVariables: CreateBookingsWithSquareInvoiceMutationVariables =
  {
    ...requestVariables,
    discount: {
      customerEmail: userDetailsSquareInvoice.email,
      discountCode: "TEST_CODE",
    },
  };

export const createBookingsWithDiscountedSquareInvoiceRequestSuccess: MockedResponse<CreateBookingsWithSquareInvoiceMutation> =
  {
    request: {
      query: CreateBookingsWithSquareInvoiceDocument,
      variables: discountRequestVariables,
    },
    result: {
      data: {
        createBookingsWithSquareInvoice: {
          __typename: "CreateBookingsResult",
          bookings: [{ _id: successConfirmBookingWithSquareInvoiceId }],
        },
      },
    },
  };
const requestVariablesWithoutCrmData: CreateBookingsWithSquareInvoiceMutationVariables =
  {
    bookings: [
      {
        bookingQuestions: getBookingQuestionInput({
          bookingQuestionData: {},
          bookingQuestions: organisation.externalBookingQuestions,
          pickedServiceId: defaultService._id,
        }),
        customer: {
          ...userDetailsSquareInvoiceWithoutCrmData,
          crmFieldData: [],
        },
        organisationId: organisation._id,
        reservedSpaceId: successReservedSpaceWithSquareInvoiceId,
        timezone: timezone,
      },
    ],
    bookingToolId: defaultBookingTool._id,
    cardToken: "conf:mock",
    createdSource: "test-app",
    locale: "en-GB",
    squareBuyerVerificationToken: "verf:mock",
  };

export const createBookingsWithSquareInvoiceWithoutCrmDataRequestSuccess: MockedResponse<CreateBookingsWithSquareInvoiceMutation> =
  {
    request: {
      query: CreateBookingsWithSquareInvoiceDocument,
      variables: requestVariablesWithoutCrmData,
    },
    result: {
      data: {
        createBookingsWithSquareInvoice: {
          __typename: "CreateBookingsResult",
          bookings: [{ _id: successConfirmBookingWithSquareInvoiceId }],
        },
      },
    },
  };

const recurringBookingPaymentVariables: CreateBookingsWithSquareInvoiceMutationVariables =
  {
    bookings: [
      {
        bookingIds: defaultRecurrence.bookings.map((booking) => booking._id),
        bookingQuestions: getBookingQuestionInput({
          bookingQuestionData: {},
          bookingQuestions: testOrganisationSquare.externalBookingQuestions,
          pickedServiceId: defaultService._id,
        }),
        customer: {
          ...userDetailsSquareInvoice,
          crmFieldData: [
            { id: "date_field", value: format(today, "yyyy-MM-dd") },
            { id: "optional_field", value: "" },
            { id: "text_field", value: "this is a text field" },
          ],
        },
        organisationId: testOrganisationSquare._id,
        reservedSpaceId: successReservedSpaceWithSquareInvoiceId,
        timezone: timezone,
      },
    ],
    bookingToolId: defaultBookingTool._id,
    cardToken: "conf:mock",
    createdSource: "test-app",
    locale: "en-GB",
    squareBuyerVerificationToken: "verf:mock",
  };

export const createRecurringBookingsWithSquareInvoiceRequestSuccess: MockedResponse<CreateBookingsWithSquareInvoiceMutation> =
  {
    request: {
      query: CreateBookingsWithSquareInvoiceDocument,
      variables: recurringBookingPaymentVariables,
    },
    result: {
      data: {
        createBookingsWithSquareInvoice: {
          __typename: "CreateBookingsResult",
          bookings: [{ _id: successConfirmBookingWithSquareInvoiceId }],
        },
      },
    },
  };

export const createBookingsWithSquareInvoiceRequestError: MockedResponse<CreateBookingsWithSquareInvoiceMutation> =
  {
    request: {
      query: CreateBookingsWithSquareInvoiceDocument,
      variables: requestVariables,
    },
    result: { errors: [new GraphQLError("Failed to create bookings")] },
  };

export const invalidOrAlteredCreateBookingsWithSquareInvoiceRequestError: MockedResponse<CreateBookingsWithSquareInvoiceMutation> =
  {
    request: {
      query: CreateBookingsWithSquareInvoiceDocument,
      variables: requestVariables,
    },
    result: { errors: [new Errors.UserInputError("Something's changed")] },
  };

export const badCardCreateBookingsWithSquareInvoiceRequestError: MockedResponse<CreateBookingsWithSquareInvoiceMutation> =
  {
    request: {
      query: CreateBookingsWithSquareInvoiceDocument,
      variables: requestVariables,
    },
    result: {
      errors: [new Errors.CardPaymentError("Insufficient funds", 402)],
    },
  };
